import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Pencil, Plus, Send, Star, Trash2, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatDate, formatDateTime, formatPrice, ORDER_STATUS, ORDER_STATUS_OPTIONS, PAYMENT_STATUS } from "@/lib/format";
import { AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, TableWrap, Td, Th, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";

function useAdminList(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .get(url, { params })
      .then(({ data: payload }) => setData(payload))
      .finally(() => setLoading(false));
  };
  useEffect(load, [url, JSON.stringify(params || {})]);
  return { data, loading, reload: load };
}

function Loading() {
  return (
    <div className="flex justify-center py-14">
      <Loader2 className="h-6 w-6 animate-spin text-brand" />
    </div>
  );
}

/* ------------------------------------------------------------------ catégories */
const EMPTY_CATEGORY = {
  name: "", slug: "", description: "", icon: "package", color: "#0052FF", image_url: "", order: 0,
  is_active: true, featured: true,
};

export function AdminCategories() {
  const { data, loading, reload } = useAdminList("/admin/categories");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CATEGORY);
  const bulk = useBulkDelete("/admin/categories/bulk-delete", reload, "catégorie(s)");

  const submit = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order || 0) };
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Catégorie enregistrée");
      setOpen(false);
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (category) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try {
      await api.delete(`/admin/categories/${category.id}`);
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard
      title="Catégories"
      description={`${data?.length || 0} catégorie(s)`}
      action={
        <button
          data-testid="admin-add-category"
          onClick={() => {
            setEditing(null);
            setForm({ ...EMPTY_CATEGORY, order: data?.length || 0 });
            setOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-dark"
        >
          <Plus className="h-3.5 w-3.5" /> Nouvelle catégorie
        </button>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-categories" />
        <TableWrap>
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-categories"
                    checked={(data || []).length > 0 && bulk.selected.length >= (data || []).length}
                    onChange={() => bulk.toggleAll((data || []).map((c) => c.id))} />
                </Th>
                <Th>Nom</Th>
                <Th>Slug</Th>
                <Th>Icône</Th>
                <Th>Ordre</Th>
                <Th>Statut</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-categories-table">
              {(data || []).map((category) => (
                <tr key={category.id} className="border-b border-line last:border-0">
                  <Td>
                    <SelectCheckbox testId={`bulk-cat-${category.id}`}
                      checked={bulk.selected.includes(category.id)} onChange={() => bulk.toggle(category.id)} />
                  </Td>
                  <Td>
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 rounded" style={{ backgroundColor: category.color }} />
                      <span className="font-semibold">{category.name}</span>
                    </span>
                  </Td>
                  <Td className="text-slate-500">/{category.slug}</Td>
                  <Td className="text-slate-500">{category.icon}</Td>
                  <Td className="text-slate-500">{category.order}</Td>
                  <Td><Pill tone={category.is_active ? "green" : "slate"}>{category.is_active ? "Active" : "Inactive"}</Pill></Td>
                  <Td className="text-right">
                    <span className="flex justify-end gap-1.5">
                      <button
                        data-testid={`admin-edit-category-${category.slug}`}
                        onClick={() => { setEditing(category); setForm({ ...EMPTY_CATEGORY, ...category }); setOpen(true); }}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => remove(category)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editing ? "Modifier la catégorie" : "Nouvelle catégorie"}>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-category-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom">
              <input data-testid="category-form-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Slug (optionnel)">
              <input value={form.slug || ""} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <Field label="Description">
            <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={textareaCls} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Icône (lucide)" hint="monitor, shield-check, palette...">
              <input value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Couleur">
              <input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} className="h-11 w-full rounded-lg border border-line" />
            </Field>
            <Field label="Ordre">
              <input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-brand" />
            Catégorie active
          </label>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-ink">Annuler</button>
            <button type="submit" data-testid="category-form-submit" className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white">
              <Check className="h-4 w-4" /> Enregistrer
            </button>
          </div>
        </form>
      </AdminModal>
    </AdminCard>
  );
}

/* ------------------------------------------------------------------ commandes */
export function AdminOrders() {
  const [query, setQuery] = useState({ q: "", status: "", pending: false });
  const { data, loading, reload } = useAdminList("/admin/orders", { ...query, limit: 100 });
  const bulk = useBulkDelete("/admin/orders/bulk-delete", reload, "commande(s)");

  const updateStatus = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.id}`, { status });
      toast.success("Commande mise à jour");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const removeOrder = async (order) => {
    if (!window.confirm(`Supprimer définitivement la commande ${order.order_number} ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success("Commande supprimée");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard
      title="Commandes"
      description={`${data?.total || 0} commande(s)`}
      action={
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-pending-filter"
            onClick={() => setQuery((q) => ({ ...q, pending: !q.pending }))}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors duration-200 ${
              query.pending ? "border-amber-400 bg-amber-50 text-amber-700" : "border-line text-slate-600 hover:border-amber-300"
            }`}
          >
            Commandes à traiter
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-black text-white">
              {data?.pending_total ?? 0}
            </span>
          </button>
          <input
            data-testid="admin-order-search"
            value={query.q}
            onChange={(e) => setQuery((q) => ({ ...q, q: e.target.value }))}
            placeholder="N° ou client..."
            className="h-10 w-40 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
          />
          <select
            data-testid="admin-order-status-filter"
            value={query.status}
            onChange={(e) => setQuery((q) => ({ ...q, status: e.target.value }))}
            className="h-10 rounded-lg border border-line px-3 text-sm"
          >
            <option value="">Tous</option>
            {ORDER_STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>{ORDER_STATUS[value].label}</option>
            ))}
          </select>
        </div>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-orders" />
        <TableWrap>
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-orders"
                    checked={(data?.items || []).length > 0 && bulk.selected.length >= (data?.items || []).length}
                    onChange={() => bulk.toggleAll((data?.items || []).map((o) => o.id))} />
                </Th>
                <Th>Commande</Th>
                <Th>Client</Th>
                <Th>Date</Th>
                <Th>Paiement</Th>
                <Th>Statut</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-orders-table">
              {(data?.items || []).map((order) => (
                <tr key={order.id} className="border-b border-line last:border-0">
                  <Td>
                    <SelectCheckbox testId={`bulk-order-${order.order_number}`}
                      checked={bulk.selected.includes(order.id)} onChange={() => bulk.toggle(order.id)} />
                  </Td>
                  <Td className="font-bold">
                    {order.order_number}
                    {order.requires_activation && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                        À activer
                      </span>
                    )}
                  </Td>
                  <Td className="text-slate-500">
                    {order.customer?.name}
                    <span className="block text-[11px] text-slate-400">{order.customer?.email}</span>
                  </Td>
                  <Td className="text-slate-500">{formatDate(order.created_at)}</Td>
                  <Td>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${(PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending).className}`}>
                      {(PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending).label}
                    </span>
                  </Td>
                  <Td>
                    <select
                      data-testid={`order-status-${order.order_number}`}
                      value={order.status}
                      onChange={(e) => updateStatus(order, e.target.value)}
                      className="h-8 rounded-lg border border-line px-2 text-xs font-semibold"
                    >
                      {ORDER_STATUS_OPTIONS.map((value) => (
                        <option key={value} value={value}>{ORDER_STATUS[value].label}</option>
                      ))}
                      {order.status === "completed" && <option value="completed">Livrée</option>}
                    </select>
                  </Td>
                  <Td className="text-right font-bold">{formatPrice(order.total, "XOF")}</Td>
                  <Td className="text-right">
                    <span className="flex items-center justify-end gap-1.5">
                      <Link
                        to={`/admin/commandes/${order.id}`}
                        data-testid={`order-view-${order.order_number}`}
                        className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-dark"
                      >
                        Gérer
                      </Link>
                      <button
                        type="button"
                        data-testid={`order-delete-${order.order_number}`}
                        onClick={() => removeOrder(order)}
                        title="Supprimer la commande"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}

    </AdminCard>
  );
}

/* ------------------------------------------------------------------ clients */
export function AdminCustomers() {
  const [query, setQuery] = useState("");
  const { data, loading, reload } = useAdminList("/admin/users", { q: query });
  const bulk = useBulkDelete("/admin/users/bulk-delete", reload, "compte(s)");

  const setRole = async (user, role) => {
    try {
      await api.put(`/admin/users/${user.id}`, { role });
      toast.success("Rôle mis à jour");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const toggleBlock = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { is_blocked: !user.is_blocked });
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${user.name} (${user.email}) ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("Compte supprimé");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard
      title="Clients & utilisateurs"
      description={`${data?.length || 0} compte(s)`}
      action={
        <input
          data-testid="admin-customer-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher..."
          className="h-10 w-44 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
        />
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-customers" />
        <TableWrap>
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-customers"
                    checked={(data || []).filter((u) => u.role !== "admin").length > 0 &&
                      bulk.selected.length >= (data || []).filter((u) => u.role !== "admin").length}
                    onChange={() => bulk.toggleAll((data || []).filter((u) => u.role !== "admin").map((u) => u.id))} />
                </Th>
                <Th>Client</Th>
                <Th>Inscription</Th>
                <Th>Commandes</Th>
                <Th>Total dépensé</Th>
                <Th>Rôle</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-customers-table">
              {(data || []).map((user) => (
                <tr key={user.id} className="border-b border-line last:border-0">
                  <Td>
                    {user.role !== "admin" && (
                      <SelectCheckbox testId={`bulk-user-${user.email}`}
                        checked={bulk.selected.includes(user.id)} onChange={() => bulk.toggle(user.id)} />
                    )}
                  </Td>
                  <Td>
                    <span className="font-semibold">{user.name}</span>
                    <span className="block text-[11px] text-slate-400">{user.email}</span>
                  </Td>
                  <Td className="text-slate-500">{formatDate(user.created_at)}</Td>
                  <Td className="text-slate-500">{user.orders_count}</Td>
                  <Td className="font-bold">{formatPrice(user.total_spent, "XOF")}</Td>
                  <Td>
                    <select
                      data-testid={`user-role-${user.email}`}
                      value={user.role}
                      onChange={(e) => setRole(user, e.target.value)}
                      className="h-8 rounded-lg border border-line px-2 text-xs font-semibold"
                    >
                      <option value="customer">Client</option>
                      <option value="admin">Administrateur</option>
                    </select>
                  </Td>
                  <Td className="text-right">
                    <span className="flex items-center justify-end gap-1.5">
                      <button
                        data-testid={`user-block-${user.email}`}
                        onClick={() => toggleBlock(user)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${
                          user.is_blocked ? "border-emerald-200 text-emerald-700" : "border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                        }`}
                      >
                        {user.is_blocked ? "Réactiver" : "Suspendre"}
                      </button>
                      {user.role !== "admin" && (
                        <button
                          type="button"
                          data-testid={`user-delete-${user.email}`}
                          onClick={() => removeUser(user)}
                          title="Supprimer le compte"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}
    </AdminCard>
  );
}

/* ------------------------------------------------------------------ avis */
export function AdminReviews() {
  const { data, loading, reload } = useAdminList("/admin/reviews");
  const bulk = useBulkDelete("/admin/reviews/bulk-delete", reload, "avis");
  const [drafts, setDrafts] = useState({});
  const [openReply, setOpenReply] = useState({});
  const [savingReply, setSavingReply] = useState("");

  const saveReply = async (review) => {
    setSavingReply(review.id);
    try {
      await api.post(`/admin/reviews/${review.id}/reply`, {
        reply: (drafts[review.id] ?? review.admin_reply ?? "").trim(),
      });
      toast.success("Réponse enregistrée");
      setOpenReply((o) => ({ ...o, [review.id]: false }));
      reload();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSavingReply("");
    }
  };

  const moderate = async (review, isApproved) => {
    try {
      await api.put(`/admin/reviews/${review.id}`, { is_approved: isApproved });
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (review) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${review.id}`);
    reload();
  };

  return (
    <AdminCard title="Avis clients" description={`${data?.length || 0} avis`}>
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-reviews" />
        <div className="space-y-3" data-testid="admin-reviews-list">
          {(data || []).map((review) => (
            <div key={review.id} className="rounded-xl border border-line p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <SelectCheckbox testId={`bulk-review-${review.id}`}
                    checked={bulk.selected.includes(review.id)} onChange={() => bulk.toggle(review.id)} />
                  <span className="font-semibold text-ink">{review.author_name}</span>
                  <span className="flex items-center gap-0.5 text-amber-400">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <Star key={i} className="h-3 w-3" fill="currentColor" />
                    ))}
                  </span>
                  {review.is_demo && <Pill tone="amber">Démo</Pill>}
                  {review.verified_purchase && <Pill tone="blue">Achat vérifié</Pill>}
                  <Pill tone={review.is_approved ? "green" : "slate"}>
                    {review.is_approved ? "Publié" : "Masqué"}
                  </Pill>
                </span>
                <span className="flex gap-1.5">
                  <button
                    data-testid={`review-toggle-${review.id}`}
                    onClick={() => moderate(review, !review.is_approved)}
                    className="rounded-lg border border-line px-3 py-1.5 text-xs font-bold text-ink hover:border-brand hover:text-brand"
                  >
                    {review.is_approved ? "Masquer" : "Publier"}
                  </button>
                  <button
                    data-testid={`review-delete-${review.id}`}
                    onClick={() => remove(review)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">{review.comment}</p>
              {review.photo_url && (
                <a href={fileUrl(review.photo_url)} target="_blank" rel="noreferrer">
                  <img src={fileUrl(review.photo_url)} alt="Photo du client" className="mt-2 h-16 w-16 rounded-lg border border-line object-cover" />
                </a>
              )}
              {review.admin_reply && !openReply[review.id] && (
                <div className="mt-2 rounded-lg border-l-2 border-brand bg-brand-soft p-3" data-testid={`review-reply-display-${review.id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-brand">Réponse ISMO-TECH</p>
                  <p className="mt-1 text-sm text-slate-600">{review.admin_reply}</p>
                </div>
              )}
              {openReply[review.id] ? (
                <div className="mt-2">
                  <textarea
                    data-testid={`review-reply-input-${review.id}`}
                    rows={2}
                    value={drafts[review.id] ?? review.admin_reply ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [review.id]: e.target.value }))}
                    placeholder="Votre réponse publique (laisser vide pour supprimer)..."
                    className={textareaCls}
                  />
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      data-testid={`review-reply-save-${review.id}`}
                      onClick={() => saveReply(review)}
                      disabled={savingReply === review.id}
                      className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
                    >
                      {savingReply === review.id ? "Envoi..." : "Enregistrer la réponse"}
                    </button>
                    <button
                      onClick={() => setOpenReply((o) => ({ ...o, [review.id]: false }))}
                      className="rounded-lg border border-line px-3 py-1.5 text-xs font-bold text-slate-500"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid={`review-reply-btn-${review.id}`}
                  onClick={() => setOpenReply((o) => ({ ...o, [review.id]: true }))}
                  className="mt-2 text-xs font-bold text-brand hover:underline"
                >
                  {review.admin_reply ? "Modifier la réponse" : "Répondre publiquement"}
                </button>
              )}
              <p className="mt-1.5 text-[11px] uppercase tracking-wide text-slate-400">
                {review.product_name} · {formatDate(review.created_at)}
              </p>
            </div>
          ))}
        </div>
        </>
      )}
    </AdminCard>
  );
}

/* ------------------------------------------------------------------ tickets */
export function AdminTickets() {
  const { data, loading, reload } = useAdminList("/admin/tickets");
  const bulk = useBulkDelete("/admin/tickets/bulk-delete", reload, "ticket(s)");
  const [reply, setReply] = useState({});

  const send = async (ticket) => {
    const message = reply[ticket.id];
    if (!message?.trim()) return;
    try {
      await api.post(`/admin/tickets/${ticket.id}/reply`, { message });
      setReply((r) => ({ ...r, [ticket.id]: "" }));
      toast.success("Réponse envoyée");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const setStatus = async (ticket, status) => {
    await api.put(`/admin/tickets/${ticket.id}`, { status });
    reload();
  };

  const removeTicket = async (ticket) => {
    if (!window.confirm(`Supprimer définitivement le ticket ${ticket.reference} ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/tickets/${ticket.id}`);
      toast.success("Ticket supprimé");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard title="Tickets support" description={`${data?.length || 0} ticket(s)`}>
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-tickets" />
        <div className="space-y-3" data-testid="admin-tickets-list">
          {(data || []).map((ticket) => (
            <div key={ticket.id} className="rounded-xl border border-line p-4" data-testid={`admin-ticket-${ticket.reference}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <SelectCheckbox testId={`bulk-ticket-${ticket.id}`}
                    checked={bulk.selected.includes(ticket.id)} onChange={() => bulk.toggle(ticket.id)} />
                  <span className="flex items-center gap-2">
                  <SelectCheckbox testId={`bulk-ticket-${ticket.id}`}
                    checked={bulk.selected.includes(ticket.id)} onChange={() => bulk.toggle(ticket.id)} />
                  <span>
                  <span className="font-semibold text-ink">{ticket.subject}</span>
                  <span className="block text-[11px] text-slate-400">
                    {ticket.reference} · {ticket.email} · {formatDateTime(ticket.created_at)}
                  </span>
                </span>
                </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <select
                    data-testid={`ticket-status-${ticket.reference}`}
                    value={ticket.status}
                    onChange={(e) => setStatus(ticket, e.target.value)}
                    className="h-8 rounded-lg border border-line px-2 text-xs font-semibold"
                  >
                    <option value="open">Ouvert</option>
                    <option value="answered">Répondu</option>
                    <option value="closed">Fermé</option>
                  </select>
                  <button
                    type="button"
                    data-testid={`ticket-delete-${ticket.reference}`}
                    onClick={() => removeTicket(ticket)}
                    title="Supprimer le ticket"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 transition-colors duration-150 hover:border-rose-300 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>

              <div className="mt-3 space-y-2">
                {ticket.messages?.map((message, i) => (
                  <div
                    key={i}
                    className={`rounded-lg p-3 text-xs leading-relaxed ${
                      message.author === "support" ? "bg-brand-soft text-brand" : "bg-slate-50 text-slate-600"
                    }`}
                  >
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide opacity-70">
                      {message.name} · {formatDateTime(message.created_at)}
                    </span>
                    {message.message}
                  </div>
                ))}
              </div>

              <div className="mt-3 flex gap-2">
                <input
                  data-testid={`admin-ticket-reply-${ticket.reference}`}
                  value={reply[ticket.id] || ""}
                  onChange={(e) => setReply((r) => ({ ...r, [ticket.id]: e.target.value }))}
                  placeholder="Répondre au client..."
                  className="h-10 flex-1 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
                />
                <button
                  data-testid={`admin-ticket-send-${ticket.reference}`}
                  onClick={() => send(ticket)}
                  className="flex items-center gap-1.5 rounded-lg bg-brand px-4 text-xs font-bold text-white"
                >
                  <Send className="h-3.5 w-3.5" /> Envoyer
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </AdminCard>
  );
}

/* ------------------------------------------------------------------ codes promo */
export function AdminPromos() {
  const { data, loading, reload } = useAdminList("/admin/promo-codes");
  const bulk = useBulkDelete("/admin/promo-codes/bulk-delete", reload, "code(s) promo");
  const [form, setForm] = useState({ code: "", percent: 10, min_amount: 0, is_active: true, expires_at: "" });

  const submit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/admin/promo-codes", {
        ...form,
        percent: Number(form.percent),
        min_amount: Number(form.min_amount || 0),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : "",
      });
      setForm({ code: "", percent: 10, min_amount: 0, is_active: true, expires_at: "" });
      toast.success("Code promo créé");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (promo) => {
    await api.delete(`/admin/promo-codes/${promo.id}`);
    reload();
  };

  return (
    <div className="space-y-5">
      <AdminCard title="Nouveau code promo">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-5" data-testid="admin-promo-form">
          <Field label="Code">
            <input data-testid="promo-form-code" required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} className={inputCls} />
          </Field>
          <Field label="Réduction (%)">
            <input data-testid="promo-form-percent" type="number" min={1} max={90} value={form.percent} onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))} className={inputCls} />
          </Field>
          <Field label="Montant minimum">
            <input type="number" min={0} value={form.min_amount} onChange={(e) => setForm((f) => ({ ...f, min_amount: e.target.value }))} className={inputCls} />
          </Field>
          <Field label="Expiration">
            <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className={inputCls} />
          </Field>
          <div className="flex items-end">
            <button type="submit" data-testid="promo-form-submit" className="h-11 w-full rounded-lg bg-brand text-sm font-bold text-white">
              Créer
            </button>
          </div>
        </form>
      </AdminCard>

      <AdminCard title="Codes existants" description={`${data?.length || 0} code(s)`}>
        {loading ? (
          <Loading />
        ) : (
          <>
          <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-promos" />
          <TableWrap>
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-line text-left">
                  <Th className="w-9">
                    <SelectCheckbox testId="bulk-all-promos"
                      checked={(data || []).length > 0 && bulk.selected.length >= (data || []).length}
                      onChange={() => bulk.toggleAll((data || []).map((p) => p.id))} />
                  </Th>
                  <Th>Code</Th>
                  <Th>Réduction</Th>
                  <Th>Minimum</Th>
                  <Th>Expiration</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody data-testid="admin-promos-table">
                {(data || []).map((promo) => (
                  <tr key={promo.id} className="border-b border-line last:border-0">
                    <Td>
                      <SelectCheckbox testId={`bulk-promo-${promo.id}`}
                        checked={bulk.selected.includes(promo.id)} onChange={() => bulk.toggle(promo.id)} />
                    </Td>
                    <Td className="font-mono font-bold text-brand">{promo.code}</Td>
                    <Td>-{promo.percent}%</Td>
                    <Td className="text-slate-500">{formatPrice(promo.min_amount, "XOF")}</Td>
                    <Td className="text-slate-500">{promo.expires_at ? formatDate(promo.expires_at) : "Illimité"}</Td>
                    <Td className="text-right">
                      <button
                        data-testid={`promo-delete-${promo.code}`}
                        onClick={() => remove(promo)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          </>
        )}
      </AdminCard>
    </div>
  );
}

/* ------------------------------------------------------------------ journal */
export function AdminLogs() {
  const { data, loading, reload } = useAdminList("/admin/audit-logs", { limit: 200 });
  const bulk = useBulkDelete("/admin/audit-logs/bulk-delete", reload, "entrée(s) du journal");

  const clearAll = async () => {
    if (!window.confirm("Vider complètement le journal d'activité ?\nCette action est irréversible.")) return;
    try {
      const { data: res } = await api.delete("/admin/audit-logs");
      toast.success(`Journal vidé (${res.deleted} entrée(s))`);
      bulk.toggleAll([]);
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard
      title="Journal d'activité"
      description="Actions importantes enregistrées côté serveur"
      action={
        <button
          type="button"
          data-testid="logs-clear-btn"
          onClick={clearAll}
          className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-bold text-rose-600 transition-colors duration-150 hover:bg-rose-50"
        >
          <Trash2 className="h-3.5 w-3.5" /> Vider le journal
        </button>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-logs" />
        <TableWrap>
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-logs"
                    checked={(data || []).length > 0 && bulk.selected.length >= (data || []).length}
                    onChange={() => bulk.toggleAll((data || []).map((l) => l.id))} />
                </Th>
                <Th>Date</Th>
                <Th>Utilisateur</Th>
                <Th>Action</Th>
                <Th>Entité</Th>
                <Th>Détails</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-logs-table">
              {(data || []).map((log) => (
                <tr key={log.id} className="border-b border-line last:border-0">
                  <Td>
                    <SelectCheckbox testId={`bulk-log-${log.id}`}
                      checked={bulk.selected.includes(log.id)} onChange={() => bulk.toggle(log.id)} />
                  </Td>
                  <Td className="whitespace-nowrap text-slate-500">{formatDateTime(log.created_at)}</Td>
                  <Td className="text-slate-500">{log.user_email}</Td>
                  <Td><Pill tone="blue">{log.action}</Pill></Td>
                  <Td className="text-slate-500">{log.entity}</Td>
                  <Td className="max-w-[240px] truncate text-[11px] text-slate-400">
                    {Object.keys(log.extra || {}).length ? JSON.stringify(log.extra) : "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}
    </AdminCard>
  );
}
